
import React, { useState } from 'react';
import { ChatSession, Project } from '../types';
import { ChevronDownIcon, SettingsIcon, SparklesIcon } from './Icons';
import { SidebarAssistant } from './SidebarAssistant';

interface SidebarProps {
  assistantSession: ChatSession;
  onAssistantSendMessage: (content: string) => void;
  isProcessing: boolean;
  onOpenSettings: () => void;
  projects: Project[];
  activeProjectId: string | null;
  onProjectSelect: (id: string) => void;
  onOpenSkills: () => void;
  onOpenProjectImport: () => void;
  onCreateProject: () => void;
  onNewChat: () => void;
  onToggleVoice: () => void;
  onExpandChat: () => void;
  disableCallAgent?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  assistantSession,
  onAssistantSendMessage,
  isProcessing,
  onOpenSettings,
  projects,
  activeProjectId,
  onProjectSelect,
  onOpenSkills,
  onOpenProjectImport,
  onCreateProject,
  onNewChat,
  onToggleVoice,
  onExpandChat,
  disableCallAgent
}) => {
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const activeProject = projects.find(p => p.id === activeProjectId);

  return (
    <aside className="w-80 h-full flex flex-col bg-transparent shrink-0 select-none overflow-hidden font-['Inter'] relative">
      {/* Project Context Header */}
      <div className="px-6 pt-6 pb-4">
        <label className="text-[9px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 block">Workspace Context</label>
        <div className="relative">
          <button
            onClick={() => setShowProjectMenu(!showProjectMenu)}
            className="w-full flex items-center justify-between rounded-2xl px-4 py-3 text-left transition-all group bdat-soft-panel-strong hover:border-slate-300"
          >
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-[0_0_0_4px_rgba(59,130,246,0.14)] shrink-0"></div>
              <span className="text-xs font-bold text-slate-900 truncate">
                {activeProject ? activeProject.name : "Selecteer Project"}
              </span>
            </div>
            <div className={`text-slate-500 transition-transform duration-300 ${showProjectMenu ? 'rotate-180' : ''}`}>
              <ChevronDownIcon />
            </div>
          </button>

          {showProjectMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowProjectMenu(false)}
              ></div>
              <div className="absolute top-full left-0 right-0 mt-2 rounded-2xl bdat-soft-panel-strong z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="max-h-60 overflow-y-auto custom-scrollbar">
                  <button
                    onClick={() => {
                      onProjectSelect('');
                      setShowProjectMenu(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-xs transition-colors border-b border-slate-100 last:border-0 ${!activeProjectId ? 'bg-blue-600 text-white font-bold' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  >
                    BDAT LOBBY (Landelijke Context)
                  </button>
                  {projects.map(p => (
                    <button
                      key={p.id}
                      onClick={() => {
                        onProjectSelect(p.id);
                        setShowProjectMenu(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-xs transition-colors border-b border-slate-100 last:border-0 ${activeProjectId === p.id
                        ? 'bg-blue-600 text-white font-bold'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-px bg-slate-100 border-t border-slate-100">
                  <button
                    onClick={() => {
                      setShowProjectMenu(false);
                      onCreateProject();
                    }}
                    className="px-4 py-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-600 hover:text-slate-900 hover:bg-white transition-all"
                  >
                    Nieuw
                  </button>
                  <button
                    onClick={() => {
                      setShowProjectMenu(false);
                      onOpenProjectImport();
                    }}
                    className="px-4 py-3 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 transition-all"
                  >
                    Import
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Dedicated BDAT AI Assistant Section */}
      <div className="flex-1 flex flex-col px-4 pb-4 overflow-hidden min-h-0">
        <SidebarAssistant 
          session={assistantSession}
          onSendMessage={onAssistantSendMessage}
          isProcessing={isProcessing}
          activeProject={activeProject}
          onNewChat={onNewChat}
          onToggleVoice={onToggleVoice}
          onExpandChat={onExpandChat}
          disableCallAgent={disableCallAgent}
        />
      </div>

      {/* Footer Config */}
      <div className="px-4 py-3 shrink-0">
        <div className="flex gap-2">
          <button
            onClick={onOpenSkills}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl transition-all text-blue-700 text-[9px] font-bold border border-blue-200 bg-blue-50 hover:bg-blue-100 uppercase tracking-widest group"
          >
            <div className="transition-transform group-hover:scale-110">
              <SparklesIcon className="w-3 h-3" />
            </div>
            <span>Skills</span>
          </button>
          <button
            onClick={onOpenSettings}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl transition-all text-slate-600 text-[9px] font-bold border border-slate-200 bg-white/80 hover:bg-white uppercase tracking-widest group"
          >
            <div className="transition-transform group-hover:rotate-90 scale-75">
              <SettingsIcon />
            </div>
            <span>Config</span>
          </button>
        </div>
        <div className="mt-2 flex flex-col items-center gap-1">
          <p className="text-[6px] text-slate-400 uppercase tracking-[0.3em] font-black">
            BDAT v2.1
          </p>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
